import { useEffect, useState, useMemo } from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ModeEditOutlineTwoToneIcon from "@mui/icons-material/ModeEditOutlineTwoTone";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  styled,
  useTheme,
  TextField,
  Stack,
  InputAdornment,
  Tooltip,
  MenuItem,
} from "@mui/material";
import Refresh from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DownloadIcon from "@mui/icons-material/Download";
import subscriptionRoleLimitsService from "../../../services/subscriptionRoleLimitsService";
import { useAuth } from "../../../context/AuthContext";
import { useSubscriptionCapabilities } from "../../../hooks/useSubscriptionCapabilities";
import { filterDepartmentsForPackage } from "../../../utils/subscriptionScopeUtils";
import { formatUserRoleOptionLabel } from "../../../utils/pathRoleUtils";

import {
  deleteUserRole as deleteDoctorService,
  getAllUserRoles,
} from "../../../services/UserRoleService.js";
import { getAllStaff } from "../../../services/StaffService.js";
import { getAllDepartment } from "../../../services/departmentService.js";
import AddNewDoctor from "./AddNewRole.jsx";
import EditRole from "./EditRole.jsx";
import axios from "axios";
import dayjs from "dayjs";
import { getOrganizationName } from "../../../utils/organizationUtils";
import { downloadStyledTableExcel } from "../../../utils/patientTableExcelExport";
import {
  TABLE_ACTIONS_ROW_SX,
  TABLE_ADD_BUTTON_SX,
  TABLE_ICON_BUTTON_SX,
  TABLE_ICON_SX,
  TABLE_SEARCH_ROW_SX,
} from "../../../utils/pageLayoutStyles";

// Roles that can be created from RolesTable (excluding doctor which has its own table)
const ROLES_TABLE_ROLES = [
  "staff",
  "frontdesk",
  "pathdesk",
  "admin",
  "super_admin",
  "anesthesiologist",
  "ot_nurse",
  "circulating_ot_nurse",
  "ot_technician",
  "ot_attendant",
  "pharmacy admin",
];

// Base columns without password
const baseColumns = [
  { id: "id", label: "Staff Id" },
  { id: "name", label: "Staff Name" },
  { id: "mobile", label: "Mobile" },
  { id: "email", label: "Email" },
  { id: "aadhar", label: "Aadhar" },
  { id: "joiningDate", label: "Joining Date" },
  { id: "education", label: "Education" },
  { id: "registrationNumber", label: "Registration Number" },
  { id: "userRole", label: "Staff Role" },
  { id: "departmentName", label: "Department Name" },
];

// Helper function to get columns based on user role
const getColumns = (userRole) => {
  const normalizeRole = (role) => (role || "").toLowerCase().replace(/[_\s]+/g, "");
  const normalizedRole = normalizeRole(userRole);
  
  // Only show password for admin and super_admin roles
  const showPassword = normalizedRole === "admin" || normalizedRole === "superadmin";
  
  if (showPassword) {
    // Insert password after mobile
    const columns = [...baseColumns];
    const mobileIndex = columns.findIndex(col => col.id === "mobile");
    columns.splice(mobileIndex + 1, 0, { id: "password", label: "Password" });
    return columns;
  }
  
  return baseColumns;
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}));

const normalizeRoleNameForLimit = (name) =>
  (name || "").toLowerCase().replace(/[_\s]+/g, "_").trim();

const isCombinedStaffLimitReached = (allRoleLimits) => {
  const rolesTableRoleLimits = (allRoleLimits || []).filter((limit) => {
    const roleNameLower = normalizeRoleNameForLimit(limit.roleName);
    return ROLES_TABLE_ROLES.some(
      (rtRole) => roleNameLower === normalizeRoleNameForLimit(rtRole)
    );
  });

  let combinedAllowedUsers = 0;
  let combinedCurrentCount = 0;
  let hasUnlimitedRole = false;

  rolesTableRoleLimits.forEach((limit) => {
    const allowedUsers = limit.allowedUsers ?? limit.allowed_users ?? -1;
    const currentCount = limit.currentCount || 0;

    if (allowedUsers === -1) {
      hasUnlimitedRole = true;
    } else {
      combinedAllowedUsers += allowedUsers;
      combinedCurrentCount += currentCount;
    }
  });

  if (hasUnlimitedRole) {
    return false;
  }

  return combinedCurrentCount >= combinedAllowedUsers;
};

export default function DoctorsTable() {
  const { user } = useAuth();
  const { isPremiumPackage } = useSubscriptionCapabilities();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [fetching, setFetching] = useState({ doctor_list: true });
  const [doctor_list, setDoctorList] = useState([]);
  const theme = useTheme();
  const [open_doctor_modal, setOpenDoctorModal] = useState(false);
  const [doctor_info, setDoctorInfo] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [staffLimitReached, setStaffLimitReached] = useState(false);
  const [checkingLimit, setCheckingLimit] = useState(false);
  const [roleNameToUsernameMap, setRoleNameToUsernameMap] = useState({});
  const [departmentList, setDepartmentList] = useState([]);
  const [roleList, setRoleList] = useState([]);

  const visibleDepartmentFilters = useMemo(
    () =>
      filterDepartmentsForPackage(departmentList, {
        isPremium: isPremiumPackage,
      }),
    [departmentList, isPremiumPackage]
  );

  const resolveOrganizationId = () => {
    const orgIdFromStorage = localStorage.getItem("organizationId");
    if (orgIdFromStorage && !isNaN(orgIdFromStorage) && Number(orgIdFromStorage) > 0) {
      return Number(orgIdFromStorage);
    }
    if (user?.organizationId) {
      return user.organizationId;
    }
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        return parsedUser?.organizationId || null;
      } catch (e) {
        console.error("Error parsing stored user:", e);
      }
    }
    return null;
  };

  const checkCombinedRoleLimits = async (organizationId, staffList = null) => {
    if (!organizationId) {
      setStaffLimitReached(false);
      return;
    }

    setCheckingLimit(true);
    try {
      const allRoleLimits = await subscriptionRoleLimitsService.getAllRoleLimits(
        organizationId,
        staffList ? { staff: staffList } : {}
      );
      setStaffLimitReached(isCombinedStaffLimitReached(allRoleLimits));
    } catch (error) {
      console.error("Error checking combined role limits:", error);
      setStaffLimitReached(false);
    } finally {
      setCheckingLimit(false);
    }
  };

  useEffect(() => {
    // PRIORITY: Check localStorage.organizationId FIRST (for super_admin who gets it set during login)
    // This ensures super_admin can fetch data immediately without waiting for AuthContext update
    let orgId = null;
    const orgIdFromStorage = localStorage.getItem("organizationId");
    if (orgIdFromStorage && !isNaN(orgIdFromStorage) && Number(orgIdFromStorage) > 0) {
      orgId = Number(orgIdFromStorage);
      console.log(`[RolesTable] Found organizationId in localStorage: ${orgId}`);
    } else {
      // Fallback: Check user object from context
      orgId = user?.organizationId;
      if (!orgId) {
        // Check localStorage.user as well
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            orgId = parsedUser?.organizationId;
          } catch (e) {
            console.error("Error parsing stored user:", e);
          }
        }
      }
    }
    
    // Fetch data if organizationId is available
    // For super_admin, organizationId should be in localStorage from login
    if (orgId) {
      console.log(`[RolesTable] OrganizationId available (${orgId}), fetching data immediately`);
      fetchDoctorList();
      fetchUserRoles();
      fetchDepartments();
    } else {
        // Check if user is super_admin - they need organizationId to fetch data
        const normalizedRole = (user?.roleName || "").toLowerCase();
        const isSuperAdmin = normalizedRole === 'superadmin' || normalizedRole === 'super_admin';
        
        if (isSuperAdmin) {
          // Super admin needs organizationId - wait for it
          console.log("[RolesTable] Super admin - waiting for organizationId...");
        const handleOrgIdUpdate = (event) => {
          const { updatedUser } = event.detail;
          if (updatedUser?.organizationId) {
            console.log("[RolesTable] OrganizationId updated, fetching data");
            fetchDoctorList();
            fetchUserRoles();
            fetchDepartments();
            window.removeEventListener('userOrganizationIdUpdated', handleOrgIdUpdate);
          }
        };
        window.addEventListener('userOrganizationIdUpdated', handleOrgIdUpdate);
        
        // Fallback: check again after a delay
        const timeout = setTimeout(() => {
          const recheckOrgId = localStorage.getItem("organizationId");
          if (recheckOrgId && !isNaN(recheckOrgId) && Number(recheckOrgId) > 0) {
            console.log("[RolesTable] OrganizationId found on retry, fetching data");
            fetchDoctorList();
            fetchUserRoles();
            fetchDepartments();
          }
          window.removeEventListener('userOrganizationIdUpdated', handleOrgIdUpdate);
        }, 500);
        
        return () => {
          clearTimeout(timeout);
          window.removeEventListener('userOrganizationIdUpdated', handleOrgIdUpdate);
        };
        } else {
          // Not super_admin and no organizationId - might be base user, fetch anyway
          console.log("[RolesTable] No organizationId but not super_admin, fetching data (base user)");
          fetchDoctorList();
          fetchUserRoles();
          fetchDepartments();
        }
      }
  }, [user, user?.organizationId]);

  // Fetch user roles to create mapping from roleName to username
  const fetchUserRoles = async () => {
    try {
      const roles = await getAllUserRoles();
      // Create a mapping from roleName to username
      const mapping = {};
      roles.forEach((role) => {
        if (role.roleName && role.username) {
          mapping[role.roleName] = role.username;
        }
      });
      setRoleNameToUsernameMap(mapping);
      // Store role list for filter dropdown
      setRoleList(roles);
    } catch (error) {
      console.error("Error fetching user roles:", error);
    }
  };

  // Fetch departments from backend
  const fetchDepartments = async () => {
    try {
      const departments = await getAllDepartment();
      setDepartmentList(Array.isArray(departments) ? departments : []);
    } catch (error) {
      console.error("Error fetching departments:", error);
      setDepartmentList([]);
    }
  };

  // Fetch only active staff
 const fetchDoctorList = async () => {
  setFetching({ ...fetching, doctor_list: true });
  try {
    const data = await getAllStaff();
    // Ensure data is an array
    const dataArray = Array.isArray(data) ? data : [];
    console.log('data-doctor', dataArray); 
    // Filter active doctors only
    const activeDoctors = dataArray.filter((doctor) => doctor.is_active !== 0);
    const sorted = [...activeDoctors].sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
    setDoctorList(sorted);

    const organizationId = resolveOrganizationId();
    if (organizationId) {
      await checkCombinedRoleLimits(organizationId, dataArray);
    }
  } catch (error) {
    console.error("Error fetching staff:", error);
    // Set empty array on error to show "No records found" message
    setDoctorList([]);
  } finally {
    setFetching({ ...fetching, doctor_list: false });
  }
};

// Mark doctor as inactive on delete
const removeDoctor = async (id) => {
  setFetching({ ...fetching, remove_doctor: true });
  try {
    await deleteDoctorService(id); // Send DELETE request
    // Set is_active to 0 locally to simulate inactivation
    setDoctorList((prevList) =>
      prevList.map((doctor) =>
        doctor.id === id ? { ...doctor, is_active: 0 } : doctor
      ).filter((doctor) => doctor.is_active !== 0) // Filter out inactive doctors for display
    );
  } catch (error) {
    console.error("Failed to mark doctor as inactive", error);
    console.log("Deleting doctor with id:", id);
  }
  setFetching({ ...fetching, remove_doctor: false });
};

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const filteredRows = doctor_list.filter((d) => {
    // General search query - searches across all fields
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const searchableFields = [
        d.id, 
        d.name, 
        d.mobile, 
        d.email, 
        d.aadhar, 
        d.education, 
        d.registrationNumber,
        d.userRole, 
        d.departmentName,
        d.password,
        d.address,
        d.specialty
      ]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());
      
      // Also search in formatted joining date
      if (d.joiningDate) {
        const formattedDate = dayjs(d.joiningDate).isValid() 
          ? dayjs(d.joiningDate).format("DD-MM-YYYY").toLowerCase()
          : String(d.joiningDate).toLowerCase();
        searchableFields.push(formattedDate);
      }
      
      if (!searchableFields.some((v) => v.includes(q))) return false;
    }
    
    // Filter by any field (if provided)
    if (filterName.trim()) {
      const n = filterName.toLowerCase();
      const searchableFields = [
        d.id,
        d.name,
        d.mobile,
        d.email,
        d.aadhar,
        d.education,
        d.specialty,
        d.registrationNumber,
        d.userRole,
        d.departmentName,
        d.password,
        d.address,
      ]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());
      
      // Also search in formatted joining date
      if (d.joiningDate) {
        const formattedDate = dayjs(d.joiningDate).isValid()
          ? dayjs(d.joiningDate).format("DD-MM-YYYY").toLowerCase()
          : String(d.joiningDate).toLowerCase();
        searchableFields.push(formattedDate);
      }
      
      if (!searchableFields.some((v) => v.includes(n))) return false;
    }
    
    // Filter by role (if provided)
    if (filterRole) {
      if (String(d.userRole || "") !== filterRole) return false;
    }
    
    // Filter by department (if provided) - match by department name
    if (filterDepartment) {
      if (String(d.departmentName || "") !== filterDepartment) return false;
    }
    
    // Filter by date range (if provided)
    if (fromDate || toDate) {
      const from = fromDate ? new Date(fromDate) : null;
      const to = toDate ? new Date(toDate) : null;
      const jd = d.joiningDate ? new Date(d.joiningDate) : null;
      if (!jd) return false;
      if (from && jd < from) return false;
      if (to) {
        const toEnd = new Date(to);
        toEnd.setHours(23, 59, 59, 999);
        if (jd > toEnd) return false;
      }
    }
    
    return true;
  });

  const clearFilters = () => {
    setSearchQuery("");
    setFilterName("");
    setFilterRole("");
    setFilterDepartment("");
    setFromDate("");
    setToDate("");
  };

  const handleExport = async () => {
    const rows = filteredRows || [];
    const headers = [
      "Staff Id",
      "Staff Name",
      "Mobile",
      "Email",
      "Aadhar",
      "Joining Date",
      "Education",
      "Registration Number",
      "Staff Role",
      "Department Name",
    ];
    const tableRows = rows.map((r) => {
      const joiningDate = r.joiningDate
        ? dayjs(r.joiningDate).isValid()
          ? dayjs(r.joiningDate).format("YYYY-MM-DD")
          : r.joiningDate
        : "";
      return [
        r.id,
        r.name,
        r.mobile || "",
        r.email || "",
        r.aadhar || "",
        joiningDate,
        r.education || "",
        r.registrationNumber || "",
        roleNameToUsernameMap[r.userRole] || r.userRole || "",
        r.departmentName || "",
      ];
    });

    await downloadStyledTableExcel({
      filename: `roles_${dayjs().format("YYYY-MM-DD")}`,
      sheetName: "Staff Roles",
      title: "Staff Roles",
      organizationName: getOrganizationName(user),
      tableHeaders: headers,
      tableRows,
    });
  };

  return (
    <Grid container>
      <Grid item xs={12} sx={TABLE_ACTIONS_ROW_SX}>
        {/* Only show Add button for admin and super_admin */}
        {(() => {
          const normalizeRole = (role) => (role || "").toLowerCase().replace(/[_\s]+/g, "");
          const currentUserRole = normalizeRole(user?.roleName);
          const isAdminOrSuperAdmin = currentUserRole === "admin" || currentUserRole === "superadmin";
          
          if (isAdminOrSuperAdmin) {
            return (
              <Tooltip 
                title={staffLimitReached ? "Combined role limit reached. Cannot add more users for any role." : "Add new staff"}
              >
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    sx={TABLE_ADD_BUTTON_SX}
                    onClick={() => {
                      setOpenDoctorModal(true);
                    }}
                    disabled={staffLimitReached || checkingLimit}
                  >
                    Add Staff
                  </Button>
                </span>
              </Tooltip>
            );
          }
          return null;
        })()}
      </Grid>

      <Paper sx={{ width: "100%" }}>
        <Box sx={{ p: 1.5, pt: 1, pb: 1 }}>
          <Box sx={TABLE_SEARCH_ROW_SX}>
            <TextField
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={TABLE_ICON_SX} fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title={showFilters ? "Hide filters" : "Show filters"}>
              <IconButton
                size="small"
                sx={TABLE_ICON_BUTTON_SX}
                onClick={() => setShowFilters((v) => !v)}
              >
                <FilterAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export to Excel">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={handleExport}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton
                size="small"
                sx={TABLE_ICON_BUTTON_SX}
                onClick={async () => await fetchDoctorList()}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {showFilters && (
          <Stack spacing={1} direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 1 }}>
            <TextField
              size="small"
              label="Filter by any field"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
            />
            <TextField
              size="small"
              label="Role"
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              select
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All</MenuItem>
              {roleList
                .filter((role) => role.roleName && role.roleName.toLowerCase() !== "doctor")
                .map((role) => (
                  <MenuItem key={role.roleName} value={role.roleName}>
                    {formatUserRoleOptionLabel(role.roleName, role.username)}
                  </MenuItem>
                ))}
            </TextField>
            <TextField
              size="small"
              label="Department"
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
              select
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All</MenuItem>
              {visibleDepartmentFilters.map((dept) => (
                <MenuItem key={dept.id} value={dept.name}>
                  {dept.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="From joining date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              label="To joining date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Tooltip title="Clear filters">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={clearFilters}>
                <ClearAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          )}
        </Box>
        <TableContainer sx={{ maxHeight: 550 }}>
          <Table>
            <TableHead>
              <TableRow>
                {getColumns(user?.roleName).map((column) => (
                  <StyledTableCell
                    key={column.id}
                    align={column.align}
                    style={{ top: 57, minWidth: column.minWidth }}
                  >
                    {column.label}
                  </StyledTableCell>
                ))}
                <StyledTableCell key={"action"} style={{ top: 57 }}>
                  Action
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {doctor_list.length === 0 || fetching.doctor_list ? (
                <TableRow>
                  <TableCell colSpan={getColumns(user?.roleName).length + 1}>
                    {fetching.doctor_list && <CircularProgress size={40} />}
                    {!fetching.doctor_list && doctor_list.length === 0 && (
                      <Alert>No Doctor </Alert>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => {
                    // Check if current user is admin or super_admin for edit button
                    const normalizeRole = (role) => (role || "").toLowerCase().replace(/[_\s]+/g, "");
                    const currentUserRole = normalizeRole(user?.roleName);
                    const isAdminOrSuperAdmin = currentUserRole === "admin" || currentUserRole === "superadmin";
                    const columns = getColumns(user?.roleName);
                    
                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={row.code}
                      >
                        {columns.map((column) => {
                          let value = row[column.id];
                          
                          // Format joiningDate for display
                          if (column.id === "joiningDate" && value) {
                            value = dayjs(value).isValid() 
                              ? dayjs(value).format("DD-MM-YYYY") 
                              : value;
                          }
                          
                          // For userRole column, display friendly label (Pathology Admin for pathdesk/path admin)
                          if (column.id === "userRole") {
                            const mapped = roleNameToUsernameMap[value] || value;
                            value = formatUserRoleOptionLabel(value, mapped);
                          }
                          
                          return (
                            <TableCell key={column.id} align={column.align}>
                              {column.format && typeof value === "number"
                                ? column.format(value)
                                : (value || "-")}
                            </TableCell>
                          );
                        })}
                        <TableCell key={row.code}>
                          <Box>
                            {/* Only show Edit button for admin and super_admin */}
                            {isAdminOrSuperAdmin && (
                              <IconButton
                                size="small"
                                sx={TABLE_ICON_BUTTON_SX}
                                onClick={() => {
                                  setOpenDoctorModal(true);
                                  setDoctorInfo(row);
                                }}
                              >
                                <ModeEditOutlineTwoToneIcon fontSize="small" />
                              </IconButton>
                            )}
                            {/* <IconButton
                              sx={{ ml: 1, color: theme.palette.primary.main }}
                              onClick={async () => 
                                await removeDoctor(row.id)
                              }
                            >
                              <DeleteForeverIcon />
                            </IconButton> */}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={doctor_list.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      <EditRole
        open={open_doctor_modal}
        onClose={async () => {
          setOpenDoctorModal(false);
          setDoctorInfo(null);
          await fetchDoctorList();
        }}
        doctor_info={doctor_info}
      />
    </Grid>
  );
}
